import React, { useState } from 'react';
import { useSWRConfig } from 'swr';
import Button from '../../../common/Button/Button';
import { PickerForm } from '../PickerForm/PickerForm';
import { PickerFormItem } from '../PickerForm/PickerFormTypes';
import { useAssetLibraryEntries } from '../../../../lib/api/swrApi';
import { filterLibraryEntries, libraryEntryIdFunction, renderLibraryEntry } from '../PickerForm/PickerForm.utils';
import { useAssignEntry } from '../../../../lib/api/postApi';
import { Api } from '../../../../lib/types/apiSpec';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { ASSOCIATE_ASSET_DIALOG, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { CourseDetailsViewProps } from '../../compound/CourseDetails/CourseDetails.types';
import { Label } from '../../../common/Label/Label';
import styles from './AssociateAssetDialog.module.scss';

type AssociateAssetDialogProps = {
  selected: Api.BaseLibraryEntry[];
} & Pick<CourseDetailsViewProps, 'associateAssetDialogTitle' | 'associateAssetDialogNoItemsSelectedMessage' | 'type'>;

const AssociateAssetDialog = ({
  type,
  selected,
  associateAssetDialogTitle,
  associateAssetDialogNoItemsSelectedMessage,
}: AssociateAssetDialogProps) => {
  const { page } = useViewContext();
  const [associateDialogOpened, setAssociateDialogOpened] = useState(false);
  const { mutate } = useSWRConfig();
  const { data, isLoading } = useAssetLibraryEntries({
    shouldFetch: associateDialogOpened,
    type: Api.DocumentType.Document,
  });
  const assignEntry = useAssignEntry({ id: page.course?.id ?? page.learningPath?.id ?? '', type });

  const handleSubmit = async (values: PickerFormItem[]) => {
    await assignEntry(values.map(({ id }) => ({ id })) ?? []);
    mutate(([key]: string[]) => key.match(/^(?:.*)?\/(courses|learningpaths)\/([^/]+)$/));
    setAssociateDialogOpened(false);
  };

  return (
    <div>
      <Button className={styles.buton} variant="outlined" onClick={() => setAssociateDialogOpened(prev => !prev)}>
        <Label name={ASSOCIATE_ASSET_DIALOG.BUTTON_LABEL} />
      </Button>
      <PickerForm
        isOpen={associateDialogOpened}
        labels={{
          selectedItemsHeadingKey: ASSOCIATE_ASSET_DIALOG.SELECTED_ITEMS,
          noItemsSelectedMessage: associateAssetDialogNoItemsSelectedMessage ?? '',
          dialogTitle: associateAssetDialogTitle ?? '',
          submitButtonLabelKey: ASSOCIATE_ASSET_DIALOG.CONFIRM_BUTTON,
          assignedSummaryKey: ASSOCIATE_ASSET_DIALOG.ASSIGNED_SUMMARY,
        }}
        value={[
          {
            titleKey: ASSOCIATE_ASSET_DIALOG.TAB_LABEL,
            list: data ?? [],
            render: renderLibraryEntry,
            filterFunction: filterLibraryEntries,
            idFunction: libraryEntryIdFunction,
            searchPlaceholderKey: COMMON_LABELS.SEARCH.ASSET,
          },
        ]}
        preselectedItems={selected}
        onSubmit={handleSubmit}
        onCancel={() => setAssociateDialogOpened(false)}
        loading={isLoading}
        allowEmpty
      />
    </div>
  );
};

export default AssociateAssetDialog;
